import { useState } from 'react';
import {
  escapeDemoColorSequences as COLOR_SEQUENCES,
  escapeDemoCursorSequences as CURSOR_SEQUENCES,
  escapeDemoStyleSequences as STYLE_SEQUENCES,
  escapeDemoUi,
} from '../content/demos/escapeDemo';
import { SITE_VOICE } from '../content/siteVoice';
import { TerminalWindow } from './TerminalWindow';
import { SubsectionLabel, InfoPanel } from './shared';

export function EscapeDemo() {
  const [demoText, setDemoText] = useState(SITE_VOICE.welcomePhrase);
  const [activeColor, setActiveColor] = useState<string | null>(null);
  const [activeEffects, setActiveEffects] = useState<Set<string>>(new Set());

  const handleColorClick = (effect: string) => {
    setActiveColor(activeColor === effect ? null : effect);
  };

  const toggleEffect = (effect: string) => {
    setActiveEffects((prev) => {
      const next = new Set(prev);
      if (effect === 'reset') {
        setActiveColor(null);
        return new Set();
      }
      next.has(effect) ? next.delete(effect) : next.add(effect);
      return next;
    });
  };

  const getActiveColorObj = () =>
    COLOR_SEQUENCES.find((c) => c.effect === activeColor);

  const getTextStyle = (): React.CSSProperties => ({
    color: getActiveColorObj()?.color,
    fontWeight: activeEffects.has('bold') ? 'bold' : undefined,
    textDecoration: activeEffects.has('underline') ? 'underline' : undefined,
  });

  const getActiveSequences = () => {
    const seqs: string[] = [];
    if (activeColor) {
      const colorSeq = COLOR_SEQUENCES.find((c) => c.effect === activeColor);
      if (colorSeq) seqs.push(colorSeq.display);
    }
    activeEffects.forEach((e) => {
      const styleSeq = STYLE_SEQUENCES.find((s) => s.effect === e);
      if (styleSeq) seqs.push(styleSeq.display);
    });
    return seqs;
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-5">
          {/* 16-Color Palette */}
          <div>
            <SubsectionLabel>{escapeDemoUi.palette16}</SubsectionLabel>
            <div className="grid grid-cols-8 gap-2">
              {COLOR_SEQUENCES.slice(0, 8).map((seq) => (
                <button
                  key={seq.effect}
                  onClick={() => handleColorClick(seq.effect)}
                  className={`h-7 ${
                    activeColor === seq.effect
                      ? 'ring-2 ring-terminal-fg ring-offset-2 ring-offset-terminal-bg'
                      : ''
                  }`}
                  style={{ backgroundColor: seq.color }}
                  title={`${seq.desc} (${seq.display})`}
                />
              ))}
            </div>
            <div className="grid grid-cols-8 gap-2 mt-2">
              {COLOR_SEQUENCES.slice(8).map((seq) => (
                <button
                  key={seq.effect}
                  onClick={() => handleColorClick(seq.effect)}
                  className={`h-7 ${
                    activeColor === seq.effect
                      ? 'ring-2 ring-terminal-fg ring-offset-2 ring-offset-terminal-bg'
                      : ''
                  }`}
                  style={{ backgroundColor: seq.color }}
                  title={`${seq.desc} (${seq.display})`}
                />
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-terminal-dim mt-2 px-0.5">
              <span>{escapeDemoUi.normalRange}</span>
              <span>{escapeDemoUi.brightRange}</span>
            </div>
          </div>

          {/* Style Sequences */}
          <div>
            <SubsectionLabel>{escapeDemoUi.styleSequences}</SubsectionLabel>
            <div className="flex flex-wrap gap-2">
              {STYLE_SEQUENCES.map((seq) => (
                <button
                  key={seq.effect}
                  onClick={() => toggleEffect(seq.effect)}
                  className={`px-3 py-1.5 border transition-all text-sm ${
                    activeEffects.has(seq.effect)
                      ? 'border-terminal-fg bg-terminal-fg/10 text-terminal-fg'
                      : 'border-terminal-border text-terminal-muted hover:border-terminal-dim hover:text-terminal-fg'
                  }`}
                >
                  <code className="text-terminal-yellow text-xs">
                    {seq.display}
                  </code>
                  <span className="ml-2">{seq.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Cursor Sequences */}
          <div>
            <SubsectionLabel>{escapeDemoUi.cursorSequences}</SubsectionLabel>
            <div className="space-y-1">
              {CURSOR_SEQUENCES.map((seq) => (
                <div
                  key={seq.effect}
                  className="flex items-center justify-between px-3 py-2 border border-terminal-border text-sm"
                >
                  <code className="text-terminal-yellow text-xs">
                    {seq.display}
                  </code>
                  <span className="text-terminal-muted">{seq.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <SubsectionLabel>{escapeDemoUi.preview}</SubsectionLabel>
          <TerminalWindow>
            <div className="min-h-[180px] flex flex-col">
              <div className="text-xs text-terminal-dim mb-4">
                {getActiveSequences().length === 0
                  ? escapeDemoUi.noSequencesActive
                  : `${escapeDemoUi.activePrefix}${getActiveSequences().join(' ')}`}
              </div>
              <div className="flex-1 flex items-center justify-center">
                <span className="text-xl" style={getTextStyle()}>
                  {demoText}
                </span>
              </div>
              <input
                type="text"
                value={demoText}
                onChange={(e) => setDemoText(e.target.value)}
                placeholder={escapeDemoUi.placeholder}
                className="w-full bg-terminal-bg border border-terminal-border px-3 py-2 text-sm text-terminal-fg placeholder:text-terminal-dim focus:outline-none focus:border-terminal-muted mt-4"
              />
            </div>
          </TerminalWindow>

          {/* Color info panel */}
          {activeColor && (
            <InfoPanel className="mt-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-6 h-6"
                  style={{ backgroundColor: getActiveColorObj()?.color }}
                />
                <div>
                  <span className="text-terminal-fg">
                    {getActiveColorObj()?.desc}
                  </span>
                  <code className="text-terminal-yellow ml-2 text-xs">
                    {getActiveColorObj()?.display}
                  </code>
                </div>
              </div>
            </InfoPanel>
          )}
        </div>
      </div>

      <div className="text-terminal-muted text-sm">
        <code className="text-terminal-yellow">{escapeDemoUi.footerEsc}</code>
        {escapeDemoUi.footerMidBeforeEscWord}
        <code className="text-terminal-yellow">
          {escapeDemoUi.footerEscKeyword}
        </code>
        {escapeDemoUi.footerAfterEscKeyword}
      </div>
    </div>
  );
}
